import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const ACCESS_KEY = "mw_access";
const REFRESH_KEY = "mw_refresh";

// ── Token store (localStorage-backed, mirrors the mobile app's Bearer scheme) ──
export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY) || null;
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY) || null;
  },
  set(session) {
    if (session?.token) localStorage.setItem(ACCESS_KEY, session.token);
    if (session?.refresh_token) localStorage.setItem(REFRESH_KEY, session.refresh_token);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

// A hook the AuthProvider registers so a hard refresh failure forces logout.
let onAuthFailure = null;
export function setOnAuthFailure(cb) {
  onAuthFailure = cb;
}

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = tokenStore.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Single-flight refresh ──
// The backend revokes the whole refresh-token family if a refresh token is
// replayed. Running parallel refreshes would trigger that, so every 401 shares
// ONE in-flight refresh promise.
let refreshPromise = null;

async function doRefresh() {
  const refresh_token = tokenStore.refresh;
  if (!refresh_token) throw new Error("no_refresh");
  // Bare axios (no interceptor) so a 401 here doesn't recurse.
  const res = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token });
  tokenStore.set(res.data);
  return res.data.token;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const isAuthCall = url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/signup");

    if (status === 401 && !original._retry && !isAuthCall && tokenStore.refresh) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = doRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (e) {
        tokenStore.clear();
        if (onAuthFailure) onAuthFailure();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Convenience: normalize backend error detail into a readable message.
export function errMessage(error, fallback = "Etwas ist schiefgelaufen.") {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.hint) return d.hint;
  if (d?.error) return d.error;
  if (error?.message) return error.message;
  return fallback;
}

// Alias used across pages ported from the mobile/web spec.
export const apiError = errMessage;

// Build an authenticated URL for an uploaded object (stored as a storage path).
export function fileUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const t = tokenStore.access;
  return `${API_BASE}/files/${pathOrUrl}${t ? `?token=${encodeURIComponent(t)}` : ""}`;
}

// Multipart upload to the object-storage route. kind = photo | video | document.
export async function uploadFile(file, kind = "photo") {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`/upload?kind=${encodeURIComponent(kind)}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
